/**
 * JSBridge类 - 用于H5页面与原生App通信，同时支持在浏览器中独立使用
 * 
 * 功能说明：
 * 1. 在App内嵌环境中，通过postMessage与原生通信
 * 2. 在浏览器环境中，通过localStorage管理用户状态
 * 3. 提供统一的API接口，业务代码无需关心运行环境
 * 
 * 使用示例：
 * 
 * // 检查登录状态
 * const isLoggedIn = await jsBridge.checkLogin();
 * 
 * // 获取用户信息
 * const userInfo = await jsBridge.getUserInfo();
 * 
 * // 监听登录状态变化
 * jsBridge.on('loginStateChange', (userInfo) => {
 *   console.log('登录状态变更:', userInfo);
 * });
 * 
 * // 在浏览器环境中设置用户信息（由业务代码调用）
 * jsBridge.setUserInfo({ userId: 123, nickname: 'test' });
 * 
 * // 在浏览器环境中清除用户信息（由业务代码调用）
 * jsBridge.clearUserInfo();
 */
class JSBridge {
  constructor() {
    this.callbackMap = new Map();
    this.userInfo = null; // 存储用户信息
    this.isLoginChecked = false; // 防止重复检查
    // 检测运行环境
    this.isInApp = this.checkIsInApp();
    this.init();
  }

  // 检测是否在App内嵌环境中
  checkIsInApp() {
    try {
      // 增加对 userAgent 的判断，确保在 LnkFun App 中能被识别
      // iOS WebView 的 UA 通常包含 Mobile 和 Safari，但也可能包含自定义标识
      // 这里假设 App 的 UserAgent 会包含特定标识，或者直接依赖注入的对象
      const ua = navigator.userAgent;
      const isCustomUA = ua.includes('LnkFun') || ua.includes('MoQu'); // 替换为你们 App 的 UA 标识

      return !!(window.JSBridge || 
                (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsBridge) || 
                window.__APP__ || 
                isCustomUA);
    } catch (e) {
      console.warn('检测运行环境时出错，默认为浏览器环境:', e);
      return false;
    }
  }

  init() {
    // 暴露给 Native 调用的全局方法
    window.handleJSBridgeMessage = (message) => {
      this.handleMessage(message);
    };

    // 添加storage事件监听，用于在浏览器环境中监听登录状态变化
    window.addEventListener('storage', (e) => {
      // 监听localStorage中的用户信息变化
      if (e.key === 'userInfo') {
        try {
          const userInfo = e.newValue ? JSON.parse(e.newValue) : null;
          this.userInfo = userInfo;
          this.dispatchEvent('loginStateChange', userInfo);
        } catch (err) {
          console.warn('解析用户信息失败:', err);
        }
      }
    });

    // 无论是否检测到App环境，都添加消息监听
    // 因为环境检测可能不准确
    window.addEventListener("message", (e) => {
      // 放宽 origin 检查，或者根据实际需求处理
      // if (e.origin !== window.location.origin) return;
      this.handleMessage(e.data);
    });
  }

  // 统一处理消息逻辑
  handleMessage(data) {
    if (!data) return;
  
    
    // 如果 data 是字符串，尝试解析
    if (typeof data === 'string') {
      data = JSON.parse(data);
    }

    // 打印接收到的数据，方便调试
    console.log('JSBridge handleMessage received:', data);

    // 提取 type 和 callbackId
    let type = data.type;
    let callbackId = data.callbackId;
    let error = data.error;
    
    // 确定 resultData
    let resultData = data.data !== undefined ? data.data : data;
    
    // 兼容处理：如果 resultData 中有 type 或 callbackId，说明消息格式可能不同
    if (resultData && (resultData.type || resultData.callbackId)) {
      type = type || resultData.type;
      callbackId = callbackId || resultData.callbackId;
      error = error || resultData.error;
      // 重新确定 resultData
      resultData = resultData.data !== undefined ? resultData.data : resultData;
    }

    // 处理回调消息
    if (callbackId) {
      const callback = this.callbackMap.get(callbackId);
      if (callback) {
        if (error) {
          callback.reject(error);
        } else {
           callback.resolve(resultData);
        }
        this.callbackMap.delete(callbackId);
      }
    }
    
    // 处理事件推送（如登录状态变更）
    if (type === 'USER_LOGIN') {
      this.userInfo = resultData;
      console.log('JSBridge handleMessage: 处理登录事件，更新用户信息', resultData);
      this.dispatchEvent('loginStateChange', resultData);
    }
    
    if (type === 'USER_LOGOUT') {
      this.userInfo = null;
      console.log('JSBridge handleMessage: 处理登出事件，清空用户信息');
      this.dispatchEvent('logout', {});
    }
  }

  /**
   * 获取用户信息
   * @returns {Promise<Object|null>} 用户信息对象或null
   */
  async getUserInfo() {
    // 如果已有缓存，直接返回
    if (this.userInfo) {
      console.log('JSBridge getUserInfo: 使用缓存的用户信息', this.userInfo);
      return this.userInfo;
    }
    
    // 调用原生方法获取用户信息
    try {
      console.log('JSBridge getUserInfo: 调用原生方法获取用户信息');
      const userInfo = await this.call('getUserInfo');
      console.log('JSBridge getUserInfo: 获取用户信息结果', userInfo);
      this.userInfo = userInfo;
      return userInfo;
    } catch (error) {
      console.warn('JSBridge getUserInfo: 获取用户信息失败', error);
      return null;
    }
  }

  /**
   * 检查登录状态
   * @param {boolean} forceRefresh 是否强制刷新登录状态
   * @returns {Promise<boolean>} 是否已登录
   */
  async checkLogin(forceRefresh = false) {
    console.log('JSBridge checkLogin: 检查登录状态', { forceRefresh, isLoginChecked: this.isLoginChecked, userInfo: this.userInfo });
    if (this.isLoginChecked && !forceRefresh) {
      console.log('JSBridge checkLogin: 使用缓存的登录状态', this.userInfo !== null);
      return this.userInfo !== null;
    }
    
    try {
      console.log('JSBridge checkLogin: 调用原生方法检查登录状态');
      const result = await this.call('checkLoginStatus');
      console.log('JSBridge checkLogin: 检查登录状态结果', result);
      this.userInfo = result.userInfo || null;
      this.isLoginChecked = true;
      console.log('JSBridge checkLogin: 更新登录状态', this.userInfo !== null);
      return this.userInfo !== null;
    } catch (error) {
      console.warn('JSBridge checkLogin: 检查登录状态失败', error);
      return false;
    }
  }

  /**
   * 分享
   * @param {Object} params 分享参数
   * @param {string} params.title 标题
   * @param {string} params.content 内容
   * @param {string} params.url 链接
   * @param {string} params.imageUrl 图片链接
   * @returns {Promise<void>}
   */
  async share(params) {
    try {
      await this.call('share', params);
    } catch (error) {
      console.warn('分享失败:', error);
    }
  }

  /**
   * 关闭网页
   * @returns {Promise<void>}
   */
  async closeWebView() {
    try {
      await this.call('closeWebView');
    } catch (error) {
      console.warn('关闭网页失败:', error);
      // 浏览器环境下，直接关闭当前页面
      window.close();
    }
  }

  // 事件派发
  dispatchEvent(eventName, data) {
    const event = new CustomEvent(eventName, { detail: data });
    window.dispatchEvent(event);
  }

  // 监听事件
  on(eventName, callback) {
    window.addEventListener(eventName, (e) => callback(e.detail));
  }

  // 移除监听
  off(eventName, callback) {
    window.removeEventListener(eventName, callback);
  }

  // 调用原生方法（App环境）或模拟方法（浏览器环境）
  call(method, params) {
    try {
      // 在App环境中通过JSBridge调用原生方法
      if (this.isInApp) {
        return new Promise((resolve, reject) => {
          const callbackId = `${method}_${Date.now()}_${Math.random()}`;
          this.callbackMap.set(callbackId, { resolve, reject });
          
          const message = {
            method,
            params,
            callbackId
          };

          // iOS WKWebView
          if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsBridge) {
            window.webkit.messageHandlers.jsBridge.postMessage(message);
          } 
          // Android WebView (addJavascriptInterface)
          else if (window.JSBridge && typeof window.JSBridge.postMessage === 'function') {
            // Android 接口接收字符串
            window.JSBridge.postMessage(JSON.stringify(message));
          }
          // 兜底：尝试使用 window.postMessage (可能被 Native 拦截)
          else {
            console.warn('未找到原生桥接对象，尝试使用 window.postMessage');
            window.postMessage(message, '*');
          }
          
          // 设置超时
          setTimeout(() => {
            if (this.callbackMap.has(callbackId)) {
              this.callbackMap.delete(callbackId);
              reject(new Error(`调用${method}超时`));
            }
          }, 10000);
        });
      } else {
        // 在浏览器环境中使用模拟数据或从localStorage获取
        return this.callBrowserMethod(method);
      }
    } catch (error) {
      console.error(`调用方法${method}时发生错误:`, error);
      // 提供降级处理
      return Promise.reject(error);
    }
  }

  // 浏览器环境的方法实现
  async callBrowserMethod(method) {
    try {
      switch (method) {
        case 'getUserInfo': {
          // 从localStorage获取用户信息
          const userInfoStr = localStorage.getItem('userInfo');
          if (userInfoStr) {
            try {
              return JSON.parse(userInfoStr);
            } catch (e) {
              console.warn('解析用户信息失败:', e);
            }
          }
          // 返回模拟数据或null
          return this.userInfo || null;
        }
          
        case 'checkLoginStatus': {
          // 检查localStorage中的用户信息
          const storedUserInfo = localStorage.getItem('userInfo');
          if (storedUserInfo) {
            try {
              const userInfo = JSON.parse(storedUserInfo);
              return { userInfo };
            } catch (e) {
              console.warn('解析用户信息失败:', e);
            }
          }
          return { userInfo: null };
        }
          
        default:
          throw new Error(`未实现的方法: ${method}`);
      }
    } catch (error) {
      console.error(`浏览器环境调用方法${method}时发生错误:`, error);
      // 提供降级处理
      return Promise.reject(error);
    }
  }

  // 在浏览器环境中设置用户信息（供外部调用）
  setUserInfo(userInfo) {
    if (!this.isInApp) {
      try {
        this.userInfo = userInfo;
        if (userInfo) {
          localStorage.setItem('userInfo', JSON.stringify(userInfo));
        } else {
          localStorage.removeItem('userInfo');
        }
        // 触发登录状态变更事件
        this.dispatchEvent('loginStateChange', userInfo);
      } catch (error) {
        console.error('设置用户信息时发生错误:', error);
      }
    }
  }

  // 在浏览器环境中清除用户信息（供外部调用）
  clearUserInfo() {
    if (!this.isInApp) {
      try {
        this.userInfo = null;
        localStorage.removeItem('userInfo');
        // 触发登出事件
        this.dispatchEvent('logout', {});
      } catch (error) {
        console.error('清除用户信息时发生错误:', error);
      }
    }
  }
}

// 全局实例
window.jsBridge = new JSBridge();